'use client';
import { memo } from 'react';
import dynamic from 'next/dynamic';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { VStack, useDisclosure } from '@chakra-ui/react';

import ContactFormSection from 'shared/ContactFormSection';
import ControllerInput from 'shared/ControllerInput';
import { configureFormData, goTo } from 'shared/lib';
import ButtonGoTo from 'shared/ButtonGoTo';
import NdaAgreement from './NdaAgreement';
import { sendMainForm } from 'shared/api';
const StatusModal = dynamic(() => import('shared/StatusModal'), { ssr: false });

import {
  FIELD_NAMES,
  defaultValues,
  validateScheme,
  mainFieldsData,
  sourceFieldsData,
  sourceAnotherOption,
  sourceOnlineSchoolOption,
} from 'constants/contactForm';
import { VM } from 'constants/validation';
import { TYPES } from 'constants/statusModal';
import { EMPTY_OBJECT, SECTIONS_IDS } from 'constants/global';

const ContactForm = () => {
  const { isOpen: isErrorOpen, onOpen: onErrorOpen, onClose: onErrorClose } = useDisclosure();
  const { isOpen: isSuccessOpen, onOpen: onSuccessOpen, onClose: onSuccessClose } = useDisclosure();
  const formApi = useForm({
    resolver: yupResolver(validateScheme),
    defaultValues,
    mode: 'onChange',
  });
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setError,
    control,
    trigger,
    reset,
  } = formApi;

  function onStatusModalClose(closeFn) {
    return () => {
      closeFn();
      goTo(SECTIONS_IDS.form, { duration: 0.6, immediate: false, offset: -110 });
    };
  }
  function handleError(field, vm) {
    setError(field, { message: vm });
    goTo(field, { offset: -200 });
  }

  async function handleFormSubmit(data) {
    try {
      const formData = await configureFormData(data);
      const res = await sendMainForm(formData);

      if (res.ok) {
        onSuccessOpen();
        reset();
      } else {
        throw res;
      }
    } catch (error) {
      const phoneError = error.messages?.[FIELD_NAMES.phone];
      const emailError = error.messages?.[FIELD_NAMES.email];

      if (phoneError && emailError) {
        handleError(FIELD_NAMES.phone, VM[12]);
        handleError(FIELD_NAMES.email, VM[3]);
      } else if (phoneError) {
        handleError(FIELD_NAMES.phone, VM[12]);
      } else if (emailError) {
        handleError(FIELD_NAMES.email, VM[3]);
      } else {
        onErrorOpen();
      }
    }
  }

  // @ts-ignore
  const informationSource = watch('informationSource');
  // @ts-ignore
  const isSourceFieldVisible = informationSource === sourceAnotherOption;
  // @ts-ignore
  const isSourceOnlineSchoolFieldVisible = informationSource === sourceOnlineSchoolOption;
  const isDisabled = Boolean(Object.keys(errors).length) || isSubmitting;

  return (
    <VStack
      as="form"
      w="full"
      align="start"
      bgColor="ourWhite"
      p={{ mobile: '24px 22px', tablet: '40px', laptop: '54px' }}
      borderRadius={{ mobile: '40px', laptop: '48px' }}
      gap={{ mobile: '32px', laptop: '40px' }}
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      <VStack w="full" align="start" gap={{ mobile: '32px', laptop: '26px' }}>
        <VStack w="full" align="start" gap={{ mobile: '32px', laptop: '40px' }}>
          {mainFieldsData.map(data => (
            <ContactFormSection key={data.title} control={control} trigger={trigger} {...data} />
          ))}
        </VStack>

        {isSourceFieldVisible && (
          <ControllerInput
            inputsDataArray={[sourceFieldsData.source]}
            control={control}
            trigger={trigger}
          />
        )}

        {isSourceOnlineSchoolFieldVisible && (
          <ControllerInput
            inputsDataArray={[sourceFieldsData.sourceOnlineSchool]}
            control={control}
            trigger={trigger}
          />
        )}
      </VStack>

      <VStack w="full" align="start" gap="12px">
        <NdaAgreement />

        <ButtonGoTo
          enableGoTo={false}
          type="submit"
          disabled={isDisabled}
          _active={EMPTY_OBJECT}
          _focusVisible={{ bgColor: '#00A372', color: '#0F151C' }}
          _hover={!isDisabled && { bgColor: '#00A372', color: '#0F151C' }}
        />
      </VStack>

      <StatusModal
        type={TYPES.success}
        isOpen={isSuccessOpen}
        onClose={onStatusModalClose(onSuccessClose)}
      />
      <StatusModal
        type={TYPES.error}
        isOpen={isErrorOpen}
        onClose={onStatusModalClose(onErrorClose)}
      />
    </VStack>
  );
};

export default memo(ContactForm);
